import { useCallback, useEffect, useRef, useState } from 'react'
import ReactFrame from 'react-frame-component'

import { Box, CircularProgress, Stack } from '@mui/material'

import SanitizeUtils from '@utils/sanitize'

import {
  editorDescriptionRootContainerSx,
  frameStyles,
  loadingWrapperSx,
} from './styles'

export interface IEditorDescriptionRefProps {
  getHeight(): number
}

export type EditorDescriptionProps = {
  description?: string
  onHeightUpdated?: (height: number) => void
}

const EditorDescription = ({
  description,
  onHeightUpdated,
}: EditorDescriptionProps) => {
  const iframeRef = useRef<any>()
  const contentRef = useRef<HTMLDivElement>()
  const updateHeightTimer = useRef<NodeJS.Timeout | null>()
  const [isContentLoaded, setIsContentLoaded] = useState(false)
  const [height, setHeight] = useState(0)
  const [head, setHead] = useState<any>()
  const [formattedDescription, setFormattedDescription] = useState(description)

  useEffect(() => {
    setFormattedDescription(description)
  }, [description])

  const handleResize = useCallback(() => {
    const scrollHeight =
      iframeRef.current?.contentDocument?.body?.scrollHeight ?? 0

    if (scrollHeight !== 0) {
      /* Note: Their seems to be a bug with how the bee editor handles height and whether to display the scrollbar
               For now, 18 additional pixels seems to fix it. */
      const newScrollHeight = Math.max(scrollHeight, 18) + 42
      setHeight(newScrollHeight)
      onHeightUpdated?.(newScrollHeight)
    }
  }, [onHeightUpdated])

  const runResizeUpdate = useCallback(() => {
    if (isContentLoaded) {
      setIsContentLoaded(false)
    }

    if (updateHeightTimer.current) {
      clearTimeout(updateHeightTimer.current)
    }

    updateHeightTimer.current = setTimeout(() => {
      handleResize()
      setIsContentLoaded(true)
    }, 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    window.addEventListener('click', handleResize)
    window.addEventListener('wheel', handleResize)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('click', handleResize)
      window.removeEventListener('wheel', handleResize)
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  const buildDescriptionHead = () => {
    const link = document.createElement('link') as HTMLLinkElement
    const style = document.createElement('style') as HTMLStyleElement

    link.rel = 'stylesheet'
    link.href =
      'https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap'

    style.innerHTML = `
      body {
        font-family: 'DM Sans', source-code-pro, Menlo, Monaco, Consolas, 'Couriea New';
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 28px;
        letter-spacing: 0.44px;
        color: #222222;
        margin: 0;
      }

      @media (max-width: 834px) {
        table,
        thead,
        tbody,
        th,
        tr,
        td {
          display: block;
        }
      
        table {
          border: none;
        }
      
        td {
          width: 100% !important;
        }
      }
    `

    let descriptionAux = description

    const descriptionStyleTag = descriptionAux?.match(/<style>(.*?)<\/style>/)
    let descriptionStyle = ''

    if (descriptionStyleTag) {
      descriptionStyle = descriptionStyleTag[0]
        .replace('<style>', '')
        .replace('</style>', '')

      descriptionAux = descriptionAux?.replace(descriptionStyleTag[0], '')

      setFormattedDescription(descriptionAux)
    }

    const descriptionLinkTags = descriptionAux?.match(/<link(.*?)>/g)
    const descriptionLinksProps: {
      rel?: string
      href?: string
      type?: string
    }[] = []

    if (descriptionLinkTags) {
      descriptionLinkTags.forEach((linkTag) => {
        const linkTagAux = linkTag.replace('<link', '').replace('>', '')

        const linkTagAttributes = linkTagAux.split(' ')

        const linkTagAttributesObject: {
          rel?: string
          href?: string
          type?: string
        } = linkTagAttributes.reduce((acc, curr) => {
          const key = curr.slice(0, curr.indexOf('='))
          const value = curr.slice(curr.indexOf('=') + 1)

          if (!key || !value) {
            return acc
          }

          return {
            ...acc,
            [key]: value?.replace(/"/g, ''),
          }
        }, {})

        descriptionLinksProps.push(linkTagAttributesObject)

        descriptionAux = descriptionAux?.replace(linkTag, '')
      })

      setFormattedDescription(descriptionAux)
    }

    setHead(
      <>
        <link rel="stylesheet" href={link.href} />
        {descriptionLinksProps.map((linkProps, index) => (
          <link key={index} {...linkProps} />
        ))}
        <style>
          {style.innerHTML}
          {descriptionStyle}
        </style>
      </>
    )
  }

  return (
    <Stack sx={editorDescriptionRootContainerSx({ isContentLoaded })}>
      <Box sx={loadingWrapperSx({ isContentLoaded })}>
        <CircularProgress />
      </Box>
      <ReactFrame
        ref={iframeRef}
        contentDidMount={buildDescriptionHead}
        contentDidUpdate={runResizeUpdate}
        style={{ height: isContentLoaded ? height : 0, ...frameStyles }}
        head={head}
      >
        {isContentLoaded ? (
          <Box
            ref={contentRef}
            dangerouslySetInnerHTML={{
              __html:
                SanitizeUtils.sanitizeEditorsOutputContent(
                  formattedDescription
                ),
            }}
          />
        ) : null}
      </ReactFrame>
    </Stack>
  )
}

export { EditorDescription }
